'use client'

import React, { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

interface FAQ { question: string; answer: string }

const INITIAL_VISIBLE = 6

const FAQAccordion = ({ faqs }: { faqs: FAQ[] }) => {
    const [open, setOpen] = useState<number | null>(0)
    const [showAll, setShowAll] = useState(false)

    const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_VISIBLE)
    const hasMore = faqs.length > INITIAL_VISIBLE

    return (
        <div>
            <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
                {visibleFaqs.map((faq, idx) => {
                    const isOpen = open === idx
                    return (
                        <div
                            key={idx}
                            className="rounded-2xl overflow-hidden"
                            style={{
                                background: '#FFFFFF',
                                border: '1px solid rgba(92,122,92,0.12)',
                                borderLeft: isOpen ? '3px solid #F5C542' : '3px solid transparent',
                                boxShadow: isOpen ? '0 6px 24px rgba(45,62,45,0.08)' : '0 1px 4px rgba(45,62,45,0.04)',
                                transition: 'border-left-color 0.3s ease, box-shadow 0.3s ease',
                            }}
                            itemScope itemProp="mainEntity" itemType="https://schema.org/Question"
                        >
                            {/* Trigger */}
                            <button
                                onClick={() => setOpen(isOpen ? null : idx)}
                                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-transparent border-none cursor-pointer"
                                aria-expanded={isOpen}
                            >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {/* Number badge */}
                                    <span
                                        className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black"
                                        style={{
                                            background: isOpen ? '#F5C542' : 'rgba(92,122,92,0.1)',
                                            color: isOpen ? '#2D3E2D' : '#5C7A5C',
                                            transition: 'background 0.3s ease, color 0.3s ease',
                                        }}
                                    >
                                        {idx + 1}
                                    </span>

                                    {/* Question */}
                                    <span
                                        className="text-sm font-bold leading-snug"
                                        style={{
                                            color: isOpen ? '#2D3E2D' : 'rgba(45,62,45,0.7)',
                                            fontFamily: 'Georgia, serif',
                                            transition: 'color 0.3s ease',
                                        }}
                                        itemProp="name"
                                    >
                                        {faq.question}
                                    </span>
                                </div>

                                {/* Chevron */}
                                <ChevronDown
                                    size={16}
                                    className="flex-shrink-0"
                                    style={{
                                        color: isOpen ? '#F5C542' : 'rgba(45,62,45,0.3)',
                                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s ease',
                                    }}
                                />
                            </button>

                            {/* Answer — Smooth CSS Grid row transition for opening and closing */}
                            <div
                                className="grid transition-[grid-template-rows] duration-300 ease-in-out overflow-hidden"
                                style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                            >
                                <div
                                    className="overflow-hidden"
                                    itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer"
                                >
                                    <div
                                        className="px-5 pb-5 sm:pl-14 text-sm leading-relaxed"
                                        style={{
                                            color: 'rgba(45,62,45,0.7)',
                                            borderTop: '1px solid rgba(92,122,92,0.1)',
                                            paddingTop: '0.75rem',
                                        }}
                                        itemProp="text"
                                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                                    />
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Show More / Show Less button */}
            {hasMore && (
                <div className="flex justify-center mt-8">
                    <button
                        onClick={() => setShowAll(prev => !prev)}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-200 hover:opacity-85 hover:scale-[1.03] active:scale-95"
                        style={{
                            background: '#2D3E2D',
                            color: '#F5C542',
                        }}
                    >
                        {showAll ? (
                            <>Show Less <ChevronUp size={15} /></>
                        ) : (
                            <>Show More <ChevronDown size={15} /></>
                        )}
                    </button>
                </div>
            )}
        </div>
    )
}

export default FAQAccordion
